import { useState } from "react";
import { Icon } from "../../../../components/icon/icon";
import { SwitchInput } from "../../../../components/inputs/switch";
import {
  ConfirmationEmailModal,
  getDefaultSubject,
} from "../../../../components/modal/app_modals/confirmationEmailModal";
import {
  useGetConfirmationEmailSettingsQuery,
  useUpdateConfirmationEmailSettingsMutation,
} from "../../../../redux/features/event/event.api";
import {
  ConfirmationEmailSettings,
  CreatedEvent,
} from "../../../../redux/features/event/event.types";
import { EventPermissions } from "../../../../utils/types";

const defaultSettings: ConfirmationEmailSettings = {
  enabled: false,
  subject: "",
  body: "",
  updatedAt: null,
};

const mergeSettings = (
  apiSettings?: ConfirmationEmailSettings,
  eventSettings?: ConfirmationEmailSettings,
) => ({
  ...defaultSettings,
  ...(eventSettings ?? {}),
  ...(apiSettings ?? {}),
});

export const EventConfirmationEmailSettings = ({
  event,
  refetchEvent,
  userEventPermissions,
}: {
  event: CreatedEvent;
  refetchEvent: () => void;
  userEventPermissions: EventPermissions[];
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const canManageSettings = userEventPermissions.includes("sendEmailBlasts");

  const {
    data: settingsResponse,
    isFetching,
    refetch,
  } = useGetConfirmationEmailSettingsQuery(
    { eventId: event._id },
    { skip: !event._id, refetchOnMountOrArgChange: true },
  );

  const [updateSettings, { isLoading: isSaving }] =
    useUpdateConfirmationEmailSettingsMutation();

  const settings = mergeSettings(
    settingsResponse?.data,
    event.confirmationEmailSettings,
  );

  const refetchSettings = () => {
    refetch();
    refetchEvent();
  };

  const handleToggle = async (checked?: boolean) => {
    const nextEnabled = !checked;

    if (!nextEnabled) {
      const resp = await updateSettings({
        eventId: event._id,
        payload: {
          enabled: false,
          subject: "",
          body: "",
        },
      });

      if (resp.data?.status === "success") {
        refetchSettings();
      }
      return;
    }

    if (!settings.body.trim()) {
      setModalOpen(true);
      return;
    }

    const resp = await updateSettings({
      eventId: event._id,
      payload: {
        enabled: true,
        subject: settings.subject || getDefaultSubject(event.name),
        body: settings.body,
      },
    });

    if (resp.data?.status === "success") {
      refetchSettings();
    }
  };

  return (
    <>
      <div className="flex flex-col gap-3">
        <p className="text-base font-bold text-grey-950">Confirmation Email</p>

        <div className="rounded-2xl border border-grey-200 p-3">
          <div className="flex w-full items-start gap-4 text-left font-bold text-grey-900">
            <Icon svg="mail" width={20} height={20} className="shrink-0" />

            <div className="flex-1">
              <p className="text-base font-bold">Send Confirmation Mail</p>
              <p className="text-sm font-normal leading-5 text-grey-700">
                Automatically sends an email to your guests immediately they get
                a ticket.
              </p>
            </div>

            <div className="self-center">
              <SwitchInput
                checked={settings.enabled}
                disabled={!canManageSettings || isSaving || isFetching}
                onChange={handleToggle}
              />
            </div>
          </div>

          {settings.enabled && settings.body ? (
            <div className="mt-3 flex items-center gap-3 rounded-2xl border border-grey-200 p-3">
              <p className="line-clamp-2 flex-1 whitespace-pre-line text-sm font-medium text-grey-900">
                {settings.body}
              </p>

              <button
                type="button"
                aria-label="Edit confirmation email"
                disabled={!canManageSettings}
                className="shrink-0 text-grey-950 disabled:cursor-not-allowed disabled:text-grey-400"
                onClick={() => setModalOpen(true)}
              >
                <Icon svg="pencil" width={22} height={22} />
              </button>
            </div>
          ) : undefined}
        </div>
      </div>

      <ConfirmationEmailModal
        open={modalOpen}
        handleClose={() => setModalOpen(false)}
        event={event}
        settings={settings}
        onSuccess={refetchSettings}
      />
    </>
  );
};
